import React from "react";
import { motion } from "framer-motion";
import { FaSpinner } from "react-icons/fa";

/**
 * Composant LoadingSpinner réutilisable avec différentes tailles et styles
 * @param {Object} props - Les propriétés du composant
 * @param {string} props.size - Taille du spinner: 'small', 'medium', 'large'
 * @param {string} props.color - Couleur du spinner (par défaut: 'primary')
 * @param {string} props.text - Texte à afficher sous le spinner
 * @param {boolean} props.fullScreen - Si le spinner doit prendre tout l'écran
 * @returns {JSX.Element} - Le composant LoadingSpinner
 */
const LoadingSpinner = ({
  size = "medium",
  color = "primary",
  text,
  fullScreen = false,
}) => {
  // Mapping des tailles
  const sizeMap = {
    small: "w-4 h-4",
    medium: "w-8 h-8",
    large: "w-12 h-12",
  };

  // Mapping des couleurs
  const colorMap = {
    primary: "text-blue-500",
    secondary: "text-purple-500",
    success: "text-green-500",
    warning: "text-yellow-500",
    danger: "text-red-500",
    light: "text-gray-300",
  };

  // Animation de rotation
  const spinTransition = {
    repeat: Infinity,
    ease: "linear",
    duration: 1,
  };

  const spinner = (
    <div className="flex flex-col items-center justify-center">
      <motion.div
        animate={{ rotate: 360 }}
        transition={spinTransition}
        className={`${sizeMap[size]} ${colorMap[color]}`}
      >
        <FaSpinner className="w-full h-full" />
      </motion.div>
      {text && <p className="mt-2 text-sm text-gray-400">{text}</p>}
    </div>
  );

  // Si fullScreen, on centre le spinner sur l'écran entier
  if (fullScreen) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-75 z-50">
        {spinner}
      </div>
    );
  }

  return spinner;
};

export default LoadingSpinner;
